import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ReceiptDetail } from "@/api/types";
import { Skeleton } from "@/components/Skeleton";
import { RelativeTime } from "@/components/RelativeTime";
import "./receipt-photo-slot.css";

export interface ReceiptPhotoSlotProps {
  status: ReceiptDetail["status"];
  photoUrl: string | null | undefined;
  photoDeletedAt: string | null | undefined;
  /** Receipt vendor — used for the image alt-text only. */
  vendorName?: string | null;
}

/**
 * BUG-014 / wireframes/receipt-detail.md + design/components.md §7.1.
 *
 * Renders the photo for a receipt across all four states the receipt
 * goes through. The 4:3 frame is enforced by CSS `aspect-ratio` so the
 * layout reserves the right amount of room before the image bytes
 * arrive — this is the "no layout shift" rule from the design system.
 *
 * Lightbox: tapping the photo in the `parsed` state opens a borderless
 * full-bleed overlay. We do NOT add pinch-zoom / pan inside the
 * overlay; mobile browsers already let the user pinch the page itself
 * once it's full-screen (per the brief).
 */
export function ReceiptPhotoSlot({
  status,
  photoUrl,
  photoDeletedAt,
  vendorName,
}: ReceiptPhotoSlotProps) {
  if (status === "confirmed") {
    return <PhotoDeletedStrip deletedAt={photoDeletedAt ?? null} />;
  }

  const alt = `Чек ${vendorName ?? ""}`.trim();
  const hasPhoto = typeof photoUrl === "string" && photoUrl.length > 0;

  if (status === "parsing" || status === "pending_upload") {
    return (
      <div
        className="wp-receipt-photo"
        data-state={status}
        data-testid="receipt-photo-slot"
      >
        <div className="wp-receipt-photo__frame">
          {hasPhoto ? (
            <img
              className="wp-receipt-photo__img"
              src={photoUrl as string}
              alt={alt}
              loading="lazy"
              width={375}
              height={281}
            />
          ) : (
            <Skeleton variant="block" width="100%" height="100%" />
          )}
          <div className="wp-receipt-photo__veil" aria-hidden="true">
            <span className="wp-receipt-photo__spinner" />
          </div>
        </div>
        <p className="wp-receipt-photo__caption">AI розбирає чек…</p>
      </div>
    );
  }

  if (status === "parse_failed") {
    return (
      <div
        className="wp-receipt-photo wp-receipt-photo--failed"
        data-state="parse_failed"
        data-testid="receipt-photo-slot"
      >
        <div className="wp-receipt-photo__frame">
          {hasPhoto ? (
            <img
              className="wp-receipt-photo__img"
              src={photoUrl as string}
              alt={alt}
              loading="lazy"
              width={375}
              height={281}
            />
          ) : (
            <Skeleton variant="block" width="100%" height="100%" />
          )}
        </div>
      </div>
    );
  }

  // status === "parsed"
  return (
    <ParsedPhoto photoUrl={hasPhoto ? (photoUrl as string) : null} alt={alt} />
  );
}

function ParsedPhoto({ photoUrl, alt }: { photoUrl: string | null; alt: string }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <div
      className="wp-receipt-photo"
      data-state="parsed"
      data-testid="receipt-photo-slot"
    >
      {photoUrl ? (
        <button
          type="button"
          className="wp-receipt-photo__trigger"
          onClick={() => setOpen(true)}
          aria-label="Збільшити фото чека"
        >
          <span className="wp-receipt-photo__frame">
            <img
              className="wp-receipt-photo__img"
              src={photoUrl}
              alt={alt}
              loading="lazy"
              width={375}
              height={281}
            />
          </span>
        </button>
      ) : (
        <div className="wp-receipt-photo__frame">
          <Skeleton variant="block" width="100%" height="100%" />
        </div>
      )}
      <p className="wp-receipt-photo__caption" aria-hidden="true">
        Тапни щоб збільшити
      </p>
      {open && photoUrl ? (
        <ReceiptPhotoLightbox src={photoUrl} alt={alt} onClose={close} />
      ) : null}
    </div>
  );
}

function ReceiptPhotoLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    // Lock background scroll while the overlay is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="wp-receipt-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Фото чека"
      onClick={onClose}
      data-testid="receipt-photo-lightbox"
    >
      <button
        type="button"
        className="wp-receipt-lightbox__close"
        onClick={onClose}
        aria-label="Закрити"
      >
        ✕
      </button>
      <img
        className="wp-receipt-lightbox__img"
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );
}

function PhotoDeletedStrip({ deletedAt }: { deletedAt: string | null }) {
  return (
    <div
      className="wp-receipt-photo-deleted"
      data-state="confirmed"
      data-testid="receipt-photo-slot"
    >
      <span className="wp-receipt-photo-deleted__icon" aria-hidden="true">
        🔒
      </span>
      <span className="wp-receipt-photo-deleted__text">
        {deletedAt ? (
          <>
            Фото видалене{" "}
            <RelativeTime value={deletedAt} prefix="" size="caption" />
          </>
        ) : (
          "Фото видалене"
        )}
      </span>
    </div>
  );
}
